import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import { EmptyState } from '@/components/shared/EmptyState';
import { cn } from '@/lib/utils';
import {
  Download,
  ShoppingCart,
  ArrowUpDown,
  ChevronLeft,
  ChevronRight,
  PackageSearch,
  Plus,
  X,
} from 'lucide-react';
import type { RouterOutputs } from '@/lib/trpc';
import type { SummaryFilterKey } from './StockAlertsSummaryCards';

type StockAlertItem =
  RouterOutputs['stockAlerts']['getStockIntelligence']['reorderRecommendations'][number];

// Cast-gate: `isTrim` is being added to the backend payload by the parallel
// stock-alerts backend change. Read it defensively until the shared type lands.
type StockAlertItemWithTrim = StockAlertItem & { isTrim?: boolean };

interface Props {
  items: StockAlertItem[] | undefined;
  isLoading: boolean;
  hasLocation: boolean;
  /** Card-driven filter from the summary cards (AND'd with search + category). */
  summaryFilter: SummaryFilterKey;
  /** Clears the active card filter (removable chip + empty-state CTA). */
  onClearSummaryFilter: () => void;
}

type SortField =
  | 'urgencyScore'
  | 'sku'
  | 'available'
  | 'dailySales'
  | 'daysRemaining'
  | 'effectiveCoverDays';

const PAGE_SIZE = 50;

const SUMMARY_FILTER_LABELS: Record<Exclude<SummaryFilterKey, null>, string> = {
  outOfStock: 'Out of stock',
  stockingOutSoon: 'Stocking out soon',
  lowStock: 'Low stock',
  incoming: 'Incoming stock',
};

/** Predicates mirror the backend summary-count definitions. */
function matchesSummaryFilter(
  item: StockAlertItem,
  filter: Exclude<SummaryFilterKey, null>,
): boolean {
  switch (filter) {
    case 'outOfStock':
      return item.available <= 0;
    case 'stockingOutSoon':
      return (
        item.available > 0 &&
        item.daysRemaining !== null &&
        item.daysRemaining <= 7
      );
    case 'lowStock':
      return (
        item.notifyThreshold !== null &&
        item.notifyThreshold > 0 &&
        item.available > 0 &&
        item.available <= item.notifyThreshold
      );
    case 'incoming':
      return item.incoming > 0;
    default:
      return true;
  }
}

/**
 * Item 4: never render the literal "Unknown". When a row has no category, fall
 * back to its title (truncated) or the base-SKU prefix, always muted.
 */
function styleLabel(item: StockAlertItem): string {
  if (item.category && item.category.trim().length > 0) return item.category;
  if (item.title && item.title.trim().length > 0) {
    const t = item.title.trim();
    return t.length > 32 ? `${t.slice(0, 32)}…` : t;
  }
  const prefix = item.sku.split('-')[0];
  return prefix || '-';
}

function PriorityBadge({ priority }: { priority: StockAlertItem['priority'] }) {
  // 4-tier scale: critical=destructive, high=amber, medium=cyan, low=muted.
  if (priority === 'critical') {
    return <Badge variant="destructive">Critical</Badge>;
  }
  const config: Record<string, { className: string; label: string }> = {
    high: {
      className: 'bg-amber-100 text-amber-800 border-transparent hover:bg-amber-100',
      label: 'High',
    },
    medium: {
      className: 'bg-cyan-100 text-cyan-800 border-transparent hover:bg-cyan-100',
      label: 'Medium',
    },
    low: {
      className: 'bg-muted text-muted-foreground border-transparent hover:bg-muted',
      label: 'Low',
    },
  };
  const c = config[priority] ?? config.low;
  return <Badge className={c!.className}>{c!.label}</Badge>;
}

/** RFC-4180 field escaping: quote fields containing comma, quote, or newline. */
function csvField(value: string | number): string {
  const s = String(value);
  if (/[",\n\r]/.test(s)) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
}

function exportToCsv(items: StockAlertItem[]) {
  const headers = [
    'SKU',
    'Style',
    'Size',
    'Color',
    'Available',
    'Daily Sales',
    'Days Left',
    'Incoming',
    'Effective Cover (days)',
    'Priority',
    'Urgency Score',
  ];
  const rows = items.map((i) => [
    i.sku,
    styleLabel(i),
    i.size ?? '',
    i.color ?? '',
    i.available,
    i.dailySales != null ? i.dailySales.toFixed(1) : '',
    i.daysRemaining !== null ? i.daysRemaining : '',
    i.incoming,
    i.effectiveCoverDays !== null ? i.effectiveCoverDays : '',
    i.priority,
    i.urgencyScore,
  ]);

  const csv = [headers, ...rows]
    .map((r) => r.map(csvField).join(','))
    .join('\r\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `reorder-recommendations-${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

export function ReorderRecommendations({
  items,
  isLoading,
  hasLocation,
  summaryFilter,
  onClearSummaryFilter,
}: Props) {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [sortField, setSortField] = useState<SortField>('urgencyScore');
  const [sortAsc, setSortAsc] = useState(false);
  const [page, setPage] = useState(1);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  const categories = useMemo(() => {
    if (!items) return [];
    const set = new Set(
      items.map((i) => i.category).filter(Boolean) as string[]
    );
    return Array.from(set).sort();
  }, [items]);

  const filtered = useMemo(() => {
    if (!items) return [];
    let result = items;

    if (summaryFilter) {
      result = result.filter((i) => matchesSummaryFilter(i, summaryFilter));
    }

    if (search) {
      const q = search.toLowerCase();
      result = result.filter(
        (i) =>
          i.sku.toLowerCase().includes(q) ||
          i.category?.toLowerCase().includes(q) ||
          i.title?.toLowerCase().includes(q)
      );
    }

    if (categoryFilter !== 'all') {
      result = result.filter((i) => i.category === categoryFilter);
    }

    result = [...result].sort((a, b) => {
      let aVal: number;
      let bVal: number;
      switch (sortField) {
        case 'sku':
          return sortAsc
            ? a.sku.localeCompare(b.sku)
            : b.sku.localeCompare(a.sku);
        case 'available':
          aVal = a.available;
          bVal = b.available;
          break;
        case 'dailySales':
          aVal = a.dailySales ?? 0;
          bVal = b.dailySales ?? 0;
          break;
        case 'daysRemaining':
          aVal = a.daysRemaining ?? 9999;
          bVal = b.daysRemaining ?? 9999;
          break;
        case 'effectiveCoverDays':
          aVal = a.effectiveCoverDays ?? 9999;
          bVal = b.effectiveCoverDays ?? 9999;
          break;
        default:
          aVal = a.urgencyScore;
          bVal = b.urgencyScore;
      }
      return sortAsc ? aVal - bVal : bVal - aVal;
    });

    return result;
  }, [items, search, categoryFilter, sortField, sortAsc, summaryFilter]);

  // Reset to page 1 when filters change
  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  if (safePage !== page) setPage(safePage);

  const pageItems = filtered.slice(
    (safePage - 1) * PAGE_SIZE,
    safePage * PAGE_SIZE
  );

  const filteredIds = useMemo(
    () => filtered.map((i) => i.skuId),
    [filtered]
  );
  const allFilteredSelected =
    filteredIds.length > 0 &&
    filteredIds.every((id) => selectedIds.has(id));
  const someFilteredSelected =
    filteredIds.some((id) => selectedIds.has(id)) && !allFilteredSelected;

  const toggleSelected = (skuId: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(skuId)) next.delete(skuId);
      else next.add(skuId);
      return next;
    });
  };

  const toggleAllFiltered = () => {
    setSelectedIds((prev) => {
      if (filteredIds.every((id) => prev.has(id))) {
        // Deselect just the filtered set (keep any off-view selections)
        const next = new Set(prev);
        for (const id of filteredIds) next.delete(id);
        return next;
      }
      return new Set([...prev, ...filteredIds]);
    });
  };

  const createPoFromSelection = () => {
    if (selectedIds.size === 0) return;
    // One prefill shape everywhere: send explicit { skuId, quantity } items.
    // OrderCreate still reads the legacy preselectedSkuIds shape for safety.
    navigate('/orders/new', {
      state: {
        preselectedItems: Array.from(selectedIds, (skuId) => ({ skuId, quantity: 1 })),
      },
    });
  };

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortAsc(!sortAsc);
    } else {
      setSortField(field);
      setSortAsc(false);
    }
    setPage(1);
  };

  const SortHeader = ({
    field,
    children,
    className,
  }: {
    field: SortField;
    children: React.ReactNode;
    className?: string;
  }) => (
    <TableHead
      className={`cursor-pointer select-none ${className ?? ''}`}
      onClick={() => handleSort(field)}
    >
      <div className="flex items-center gap-1">
        {children}
        <ArrowUpDown className="h-3 w-3 text-muted-foreground" />
      </div>
    </TableHead>
  );

  const isFilteredView = !!(search || categoryFilter !== 'all' || summaryFilter);
  const selectedCount = selectedIds.size;
  const COLSPAN = 10;

  return (
    <Card>
      <CardHeader className="flex flex-row items-center gap-2 pb-3">
        <ShoppingCart className="h-5 w-5 text-blue-500" />
        <div className="flex-1">
          <CardTitle className="text-base">Reorder Recommendations</CardTitle>
          <p className="text-sm text-muted-foreground">
            All SKUs with stock concerns, sorted by urgency
          </p>
        </div>
        {filtered.length > 0 && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => exportToCsv(filtered)}
          >
            <Download className="h-4 w-4 mr-1" />
            Export CSV
          </Button>
        )}
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-col sm:flex-row gap-3">
          <Input
            placeholder="Search SKU, style, or title..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            className="sm:max-w-xs"
          />
          <Select
            value={categoryFilter}
            onValueChange={(v) => {
              setCategoryFilter(v);
              setPage(1);
            }}
          >
            <SelectTrigger className="sm:w-[200px]">
              <SelectValue placeholder="All categories" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All categories</SelectItem>
              {categories.map((cat) => (
                <SelectItem key={cat} value={cat}>
                  {cat}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <div className="ml-auto text-sm text-muted-foreground self-center">
            {filtered.length} SKUs
          </div>
        </div>

        {summaryFilter && (
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground">Filtered by</span>
            <Badge
              variant="secondary"
              className="gap-1 pl-2 pr-1 font-normal"
            >
              {SUMMARY_FILTER_LABELS[summaryFilter]}
              <button
                type="button"
                onClick={onClearSummaryFilter}
                aria-label="Clear card filter"
                className="ml-0.5 rounded-sm p-0.5 hover:bg-muted-foreground/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          </div>
        )}

        {selectedCount > 0 && (
          <div className="flex flex-wrap items-center gap-3 rounded-md border bg-muted/40 px-3 py-2">
            <span className="text-sm font-medium tabular-nums">
              {selectedCount} selected
            </span>
            <Button
              size="sm"
              className="h-8 px-3 text-xs gap-1.5 bg-threadheads-purple text-white hover:bg-threadheads-purple/90"
              onClick={createPoFromSelection}
            >
              <Plus className="h-3.5 w-3.5" />
              Create PO from {selectedCount} selected
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="h-8 px-2 text-xs gap-1.5"
              onClick={() => setSelectedIds(new Set())}
            >
              <X className="h-3.5 w-3.5" />
              Clear
            </Button>
          </div>
        )}

        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-10 px-3">
                  <Checkbox
                    checked={
                      allFilteredSelected
                        ? true
                        : someFilteredSelected
                          ? 'indeterminate'
                          : false
                    }
                    onCheckedChange={toggleAllFiltered}
                    disabled={filtered.length === 0}
                    aria-label="Select all filtered SKUs"
                  />
                </TableHead>
                <TableHead className="w-[48px]"></TableHead>
                <SortHeader field="sku">SKU</SortHeader>
                <TableHead>Style</TableHead>
                <SortHeader field="available">Available</SortHeader>
                <SortHeader field="dailySales">Daily Sales</SortHeader>
                <SortHeader field="daysRemaining">Days Left</SortHeader>
                <TableHead>Incoming</TableHead>
                <SortHeader field="effectiveCoverDays">Eff. Cover</SortHeader>
                <SortHeader field="urgencyScore">Priority</SortHeader>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading && (
                <TableRow>
                  <TableCell colSpan={COLSPAN} className="h-20">
                    <div className="space-y-2">
                      <Skeleton className="h-4 w-full" />
                      <Skeleton className="h-4 w-3/4" />
                      <Skeleton className="h-4 w-1/2" />
                    </div>
                  </TableCell>
                </TableRow>
              )}
              {!isLoading && filtered.length === 0 && (
                <TableRow>
                  <TableCell colSpan={COLSPAN} className="p-0">
                    {isFilteredView ? (
                      <EmptyState
                        size="sm"
                        icon={PackageSearch}
                        title="No matching SKUs"
                        description="No reorder recommendations match your current filters."
                        action={
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              setSearch('');
                              setCategoryFilter('all');
                              onClearSummaryFilter();
                            }}
                          >
                            Clear filters
                          </Button>
                        }
                      />
                    ) : (
                      <EmptyState
                        size="sm"
                        icon={ShoppingCart}
                        title="No reorder recommendations"
                        description="Every stocked SKU is comfortably above its reorder point."
                      />
                    )}
                  </TableCell>
                </TableRow>
              )}
              {pageItems.map((item) => {
                const isTrim = (item as StockAlertItemWithTrim).isTrim === true;
                const selected = selectedIds.has(item.skuId);
                return (
                  <TableRow
                    key={item.skuId}
                    className={cn(selected && 'bg-muted/30')}
                  >
                    <TableCell className="px-3">
                      <Checkbox
                        checked={selected}
                        onCheckedChange={() => toggleSelected(item.skuId)}
                        aria-label={`Select ${item.sku}`}
                      />
                    </TableCell>
                    <TableCell>
                      {item.imageUrl ? (
                        <img
                          src={item.imageUrl}
                          alt={item.sku}
                          className="h-8 w-8 rounded object-cover"
                        />
                      ) : (
                        <div className="h-8 w-8 rounded bg-muted" />
                      )}
                    </TableCell>
                    <TableCell className="font-medium">
                      <div className="flex items-center gap-1.5">
                        {item.sku}
                        {isTrim && (
                          <Badge
                            variant="outline"
                            className="h-5 px-1.5 text-[10px] font-medium text-muted-foreground"
                          >
                            Trim
                          </Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {styleLabel(item)}
                    </TableCell>
                    <TableCell>
                      <span
                        className={
                          item.available <= 0
                            ? 'text-red-600 font-medium'
                            : item.available <= (item.notifyThreshold ?? 0)
                              ? 'text-amber-600 font-medium'
                              : ''
                        }
                      >
                        {item.available}
                      </span>
                    </TableCell>
                    <TableCell>
                      {item.dailySales != null && item.dailySales > 0 ? item.dailySales.toFixed(1) : '-'}
                    </TableCell>
                    <TableCell>
                      {item.daysRemaining !== null ? (
                        <span
                          className={
                            item.daysRemaining <= 3
                              ? 'text-red-600 font-medium'
                              : item.daysRemaining <= 7
                                ? 'text-amber-600'
                                : ''
                          }
                        >
                          {item.daysRemaining}d{!hasLocation ? '*' : ''}
                        </span>
                      ) : (
                        '-'
                      )}
                    </TableCell>
                    <TableCell>
                      {item.incoming > 0 ? (
                        <span className="text-emerald-600">{item.incoming}</span>
                      ) : (
                        '-'
                      )}
                    </TableCell>
                    <TableCell>
                      {item.effectiveCoverDays !== null
                        ? `${item.effectiveCoverDays}d`
                        : '-'}
                    </TableCell>
                    <TableCell>
                      <PriorityBadge priority={item.priority} />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between pt-2">
            <p className="text-sm text-muted-foreground">
              Showing {(safePage - 1) * PAGE_SIZE + 1}-
              {Math.min(safePage * PAGE_SIZE, filtered.length)} of{' '}
              {filtered.length}
            </p>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={safePage <= 1}
                onClick={() => setPage(safePage - 1)}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <span className="text-sm text-muted-foreground">
                Page {safePage} of {totalPages}
              </span>
              <Button
                variant="outline"
                size="sm"
                disabled={safePage >= totalPages}
                onClick={() => setPage(safePage + 1)}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
